import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Typography, Snackbar, Alert, Portal, IconButton, Tooltip, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { BlockSequence } from '../types/api';
import { MarkdownRenderer } from './MarkdownText';
import { getCategoryHexColor } from '../utils/habitColors';
import { DragSource } from './BlocksContent';
import AirMaskSnackbar from './AirMaskSnackbar';

const SQUARE_SIZE = 144;

// ─── URL shortening utilities ─────────────────────────────────────────────────

// Matches bare URLs (http/https or bare domain) NOT already inside a markdown link [text](url)
const BARE_URL_REGEX = /(?<!\]\()(?<![([])((https?:\/\/)[^\s)\]]+|(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/[^\s)\]]*)?)/g;

// Matches the URL portion inside a markdown link [text](url)
const MARKDOWN_LINK_URL_REGEX = /(\[[^\]]*\]\()(https?:\/\/[^)]+|(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(?:\/[^)]*)?)(\))/g;

function shortenUrl(url: string, maxLen = 20): string {
  let display = url.replace(/^https?:\/\//, '');
  display = display.replace(/\/$/, '');
  if (display.length <= maxLen) return display;
  return display.slice(0, maxLen) + '...';
}

function shortenIntoMap(url: string, urlMap: Map<string, string>): string {
  const shortened = shortenUrl(url);
  if (!urlMap.has(shortened) || urlMap.get(shortened) === url) {
    urlMap.set(shortened, url);
    return shortened;
  }
  // Collision – use a slightly longer slice
  const longer = shortenUrl(url, 28);
  urlMap.set(longer, url);
  return longer;
}

function applyUrlShortening(
  text: string,
  urlMap: Map<string, string>,
): string {
  // First shorten URLs inside markdown links [text](url) → [text](shortened)
  let result = text.replace(MARKDOWN_LINK_URL_REGEX, (_match, prefix, url, suffix) => {
    return prefix + shortenIntoMap(url, urlMap) + suffix;
  });
  // Then shorten remaining bare URLs
  BARE_URL_REGEX.lastIndex = 0;
  result = result.replace(BARE_URL_REGEX, (match) => shortenIntoMap(match, urlMap));
  return result;
}

function expandShortenedUrls(text: string, urlMap: Map<string, string>): string {
  if (urlMap.size === 0) return text;
  // Sort keys longest-first to avoid partial replacements
  const keys = Array.from(urlMap.keys()).sort((a, b) => b.length - a.length);
  let result = text;
  for (const short of keys) {
    const full = urlMap.get(short)!;
    // Replace all occurrences
    result = result.split(short).join(full);
  }
  return result;
}

function findShortenedUrlSpans(
  text: string,
  urlMap: Map<string, string>,
): Array<{ start: number; end: number; token: string }> {
  const spans: Array<{ start: number; end: number; token: string }> = [];
  // Longest keys first to avoid partial overlaps
  const keys = Array.from(urlMap.keys()).sort((a, b) => b.length - a.length);
  for (const key of keys) {
    let idx = 0;
    while (true) {
      const pos = text.indexOf(key, idx);
      if (pos === -1) break;
      spans.push({ start: pos, end: pos + key.length, token: key });
      idx = pos + key.length;
    }
  }
  return spans;
}

// Converts bare URLs in text to markdown [shortened](fullUrl) for display.
// Leaves existing markdown links untouched.
export function prepareForDisplay(text: string): string {
  BARE_URL_REGEX.lastIndex = 0;
  return text.replace(BARE_URL_REGEX, (match) => {
    const shortened = shortenUrl(match);
    // Ensure the full URL has a protocol for the href
    const href = /^https?:\/\//.test(match) ? match : `https://${match}`;
    return `[${shortened}](${href})`;
  });
}

const scrollbarSx = {
  overflowX: 'auto',
  width: '100%',
  minWidth: 0,
  pb: 1,
  '&::-webkit-scrollbar': { height: 8 },
  '&::-webkit-scrollbar-track': { backgroundColor: '#f0efed', borderRadius: 4 },
  '&::-webkit-scrollbar-thumb': { backgroundColor: '#c2bfba', borderRadius: 4 },
  '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a09d98' },
};

// ─── Shared block cell ────────────────────────────────────────────────────────

const BlockCell: React.FC<{
  label: string;
  color: string;
  draggable?: boolean;
  dimmed?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onTextSave?: (newText: string) => void;
}> = ({ label, color, draggable = false, dimmed = false, onDragStart, onTextSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(label);
  const [displayLabel, setDisplayLabel] = useState(label);
  const [limitSnackbar, setLimitSnackbar] = useState(false);
  const [hasShortenedUrls, setHasShortenedUrls] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const urlMapRef = useRef<Map<string, string>>(new Map());
  const draftRef = useRef<string>(label);
  const labelRef = useRef<string>(label);

  const setDraftAndRef = useCallback((value: string) => {
    draftRef.current = value;
    setDraft(value);
  }, []);

  const handleDragStart = useCallback((e: React.DragEvent) => {
    // Clone the block element into an offscreen node so the browser drag ghost
    // only captures the block itself, not anything overlapping it (e.g. a fixed navbar).
    const el = boxRef.current;
    if (el) {
      const clone = el.cloneNode(true) as HTMLElement;
      clone.style.position = 'fixed';
      clone.style.top = '-9999px';
      clone.style.left = '-9999px';
      clone.style.margin = '0';
      clone.style.pointerEvents = 'none';
      document.body.appendChild(clone);
      e.dataTransfer.setDragImage(clone, el.offsetWidth / 2, el.offsetHeight / 2);
      // Remove the clone after the browser has captured the image (next microtask).
      setTimeout(() => document.body.removeChild(clone), 0);
    }
    e.dataTransfer.effectAllowed = 'move';
    onDragStart?.(e);
  }, [onDragStart]);

  useEffect(() => {
    labelRef.current = label;
    BARE_URL_REGEX.lastIndex = 0;
    MARKDOWN_LINK_URL_REGEX.lastIndex = 0;
    urlMapRef.current.clear();
    const shortened = applyUrlShortening(label, urlMapRef.current);
    const hasUrls = shortened !== label;
    setDraftAndRef(shortened);
    setDisplayLabel(label);
    setHasShortenedUrls(hasUrls);
  }, [label, setDraftAndRef]);

  const commit = useCallback(() => {
    setEditing(false);
    const currentLabel = labelRef.current;
    // Read from the DOM directly — the most reliable source of truth at blur/Enter time.
    const raw = textareaRef.current?.value ?? draftRef.current;
    const trimmed = raw.trim();
    // Expand shortened URLs back to full before comparing/saving
    const expanded = expandShortenedUrls(trimmed, urlMapRef.current);
    if (expanded !== currentLabel) {
      setDisplayLabel(expanded);
      draftRef.current = expanded;
      setDraft(expanded);
      onTextSave?.(expanded);
    } else {
      BARE_URL_REGEX.lastIndex = 0;
      MARKDOWN_LINK_URL_REGEX.lastIndex = 0;
      urlMapRef.current.clear();
      const shortened = applyUrlShortening(currentLabel, urlMapRef.current);
      setDraftAndRef(shortened);
      setHasShortenedUrls(shortened !== currentLabel);
    }
  }, [onTextSave, setDraftAndRef]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); return; }
    if (e.key === 'Escape') {
      const currentLabel = labelRef.current;
      BARE_URL_REGEX.lastIndex = 0;
      MARKDOWN_LINK_URL_REGEX.lastIndex = 0;
      urlMapRef.current.clear();
      const shortened = applyUrlShortening(currentLabel, urlMapRef.current);
      setDraftAndRef(shortened);
      setHasShortenedUrls(shortened !== currentLabel);
      setEditing(false);
      return;
    }
    if ((e.key === 'Backspace' || e.key === 'Delete') && urlMapRef.current.size > 0) {
      const el = textareaRef.current;
      if (!el) return;
      const selStart = el.selectionStart;
      const selEnd = el.selectionEnd;
      // Determine the range that would be affected
      let delStart = selStart;
      let delEnd = selEnd;
      if (selStart === selEnd) {
        if (e.key === 'Backspace') delStart = selStart - 1;
        else delEnd = selEnd + 1;
      }
      if (delStart < 0) return;
      const spans = findShortenedUrlSpans(draftRef.current, urlMapRef.current);
      const overlapping = spans.filter(s => s.start < delEnd && s.end > delStart);
      if (overlapping.length > 0) {
        e.preventDefault();
        overlapping.sort((a, b) => a.start - b.start);
        let newDraft = draftRef.current;
        let offset = 0;
        for (const span of overlapping) {
          const adjStart = span.start - offset;
          const adjEnd = span.end - offset;
          newDraft = newDraft.slice(0, adjStart) + newDraft.slice(adjEnd);
          offset += span.end - span.start;
          urlMapRef.current.delete(span.token);
        }
        const newCursor = overlapping[0].start;
        setDraftAndRef(newDraft);
        setHasShortenedUrls(urlMapRef.current.size > 0);
        requestAnimationFrame(() => {
          textareaRef.current?.setSelectionRange(newCursor, newCursor);
        });
      }
    }
  }, [commit, setDraftAndRef]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const next = e.target.value;
    const el = textareaRef.current;
    if (!el) { setDraftAndRef(next); return; }
    // Temporarily write the new value into the DOM to measure whether it fits,
    // then immediately restore before React re-renders.
    const saved = el.value;
    el.value = next;
    const fits = el.scrollHeight <= el.clientHeight;
    el.value = saved;
    if (!fits) {
      setLimitSnackbar(true);
      return;
    }
    setDraftAndRef(next);
  }, [setDraftAndRef]);

  const handlePaste = useCallback((e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedText = e.clipboardData.getData('text');
    BARE_URL_REGEX.lastIndex = 0;
    MARKDOWN_LINK_URL_REGEX.lastIndex = 0;
    const hasBareUrl = BARE_URL_REGEX.test(pastedText);
    BARE_URL_REGEX.lastIndex = 0;
    const hasMarkdownUrl = MARKDOWN_LINK_URL_REGEX.test(pastedText);
    MARKDOWN_LINK_URL_REGEX.lastIndex = 0;
    if (!hasBareUrl && !hasMarkdownUrl) return; // no URLs, let default paste happen
    e.preventDefault();
    const shortenedPaste = applyUrlShortening(pastedText, urlMapRef.current);
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? draftRef.current.length;
    const end = el.selectionEnd ?? draftRef.current.length;
    const next = draftRef.current.slice(0, start) + shortenedPaste + draftRef.current.slice(end);
    // Check if the new value fits
    const saved = el.value;
    el.value = next;
    const fits = el.scrollHeight <= el.clientHeight;
    el.value = saved;
    if (!fits) {
      setLimitSnackbar(true);
      return;
    }
    setDraftAndRef(next);
    setHasShortenedUrls(true);
    // Restore cursor position after React re-renders
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        const pos = start + shortenedPaste.length;
        textareaRef.current.setSelectionRange(pos, pos);
      }
    });
  }, [setDraftAndRef]);

  useEffect(() => {
    if (editing) textareaRef.current?.focus();
  }, [editing]);

  const sharedTextSx = {
    color: '#000',
    textAlign: 'left' as const,
    lineHeight: 1.6,
    fontStyle: 'normal',
    fontSize: '0.75rem',
    fontFamily: 'inherit',
    wordBreak: 'break-word' as const,
    overflowWrap: 'break-word' as const,
    whiteSpace: 'pre-wrap' as const,
  };

  return (
    <>
      <Box
        ref={boxRef}
        draggable={draggable}
        onDragStart={draggable ? handleDragStart : undefined}
        onClick={() => { if (onTextSave && !editing) setEditing(true); }}
        sx={{
          width: SQUARE_SIZE,
          height: SQUARE_SIZE,
          backgroundColor: color,
          borderRadius: 1,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 1,
          cursor: onTextSave ? 'text' : draggable ? 'grab' : 'default',
          opacity: dimmed ? 0.35 : 1,
          outline: 'none',
          boxShadow: draggable ? '0 0 0 2px rgba(0,0,0,0.75)' : onTextSave ? '0 0 0 2px rgba(255,255,255,0.5)' : 'none',
          transition: 'opacity 0.2s',
          userSelect: 'none',
          overflow: 'hidden',
        }}
      >
        {editing ? (
          <Tooltip
            title={hasShortenedUrls ? (
              <span>
                {Array.from(urlMapRef.current.entries()).map(([short, full]) => (
                  <span key={short} style={{ display: 'block' }}>
                    <strong>Shortened block link:</strong><br />{full}
                  </span>
                ))}
              </span>
            ) : ''}
            placement="top"
            arrow
            disableHoverListener={!hasShortenedUrls}
            disableFocusListener
            disableTouchListener
          >
            <Box
              component="textarea"
              ref={textareaRef}
              value={draft}
              onChange={handleChange}
              onBlur={commit}
              onKeyDown={handleKeyDown}
              onPaste={handlePaste}
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
              sx={{
                ...sharedTextSx,
                width: '100%',
                height: '100%',
                background: 'transparent',
                border: 'none',
                outline: 'none',
                resize: 'none',
                overflow: 'hidden',
                p: 0,
                display: 'block',
              }}
            />
          </Tooltip>
        ) : (
          <Box
            sx={{
              ...sharedTextSx,
              userSelect: 'none',
              width: '100%',
              overflow: 'hidden',
              '& *': { color: '#000' },
              '& p': { m: 0, lineHeight: 1.6, fontSize: '0.75rem', color: '#000' },
              '& ul': { pl: 2, m: 0, listStyleType: 'disc', listStylePosition: 'inside' },
              '& ol': { pl: 2, m: 0, listStyleType: 'decimal', listStylePosition: 'inside' },
              '& li': { mb: 0 },
              '& strong': { fontWeight: 700 },
              '& em': { fontStyle: 'italic' },
              '& code': { fontFamily: 'monospace', fontSize: '0.7rem' },
              '& a': { color: '#000 !important', textDecoration: 'underline', textDecorationColor: '#000' },
            }}
          >
            <MarkdownRenderer
              components={{
                a: ({ href, children }: any) => (
                  <Tooltip
                    title={
                      <span>
                        <strong>Full link:</strong><br />{href}
                      </span>
                    }
                    placement="top"
                    arrow
                  >
                    <a href={href} target="_blank" rel="noopener noreferrer">
                      {children}
                    </a>
                  </Tooltip>
                ),
              }}
            >
              {prepareForDisplay(displayLabel)}
            </MarkdownRenderer>
          </Box>
        )}
      </Box>
      <Portal>
        <Snackbar
          open={limitSnackbar}
          autoHideDuration={3000}
          onClose={() => setLimitSnackbar(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          sx={{ zIndex: 2000 }}
        >
          <Alert severity="warning" onClose={() => setLimitSnackbar(false)} sx={{ width: '100%' }}>
            Block is full.
          </Alert>
        </Snackbar>
      </Portal>
    </>
  );
};

const HorizontalArrow: React.FC<{ color?: string }> = ({ color = '#000' }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 0.4, minWidth: 10 }}>
    <Box sx={{ width: 0, height: 0, borderTop: '5px solid transparent', borderBottom: '5px solid transparent', borderLeft: `7px solid ${color}` }} />
  </Box>
);

const HorizontalArrowWithLine: React.FC<{ color?: string; lineLength?: number }> = ({ color = '#000', lineLength = 28 }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 10 }}>
    <Box sx={{ width: lineLength, height: 2, bgcolor: color, borderRadius: 1 }} />
    <Box sx={{ width: 0, height: 0, borderTop: '5px solid transparent', borderBottom: '5px solid transparent', borderLeft: `7px solid ${color}` }} />
  </Box>
);

const VerticalArrow: React.FC<{ color?: string }> = ({ color = '#000' }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', my: 0.4, minHeight: 10 }}>
    <Box sx={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `7px solid ${color}` }} />
  </Box>
);

const VerticalArrowWithLine: React.FC<{ color?: string; lineLength?: number }> = ({ color = '#000', lineLength = 28 }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 0 }}>
    <Box sx={{ width: 2, height: lineLength, bgcolor: color, borderRadius: 1 }} />
    <Box sx={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: `7px solid ${color}` }} />
  </Box>
);

// ─── Shared scroll-chevron hook ─────────────────────────────────────────────

function useScrollChevrons(axis: 'horizontal' | 'vertical') {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrolledToEnd, setScrolledToEnd] = useState(true);
  const [scrolledToStart, setScrolledToStart] = useState(true);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [chevronGlow, setChevronGlow] = useState(false);
  const scrollDirectionRef = useRef<'start' | 'end' | null>(null);
  const rafRef = useRef<number | undefined>(undefined);
  const mouseDownRef = useRef(false);

  const stopScroll = useCallback(() => {
    scrollDirectionRef.current = null;
    if (rafRef.current !== undefined) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = undefined;
    }
  }, []);

  const startScroll = useCallback((direction: 'start' | 'end') => {
    stopScroll();
    scrollDirectionRef.current = direction;
    const step = () => {
      if (scrollDirectionRef.current !== direction) return;
      const el = scrollRef.current;
      const speed = mouseDownRef.current ? 8 : 4;
      if (el) {
        if (axis === 'horizontal') el.scrollLeft += direction === 'end' ? speed : -speed;
        else el.scrollTop += direction === 'end' ? speed : -speed;
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
  }, [stopScroll, axis]);

  const startScrollEnd = useCallback(() => startScroll('end'), [startScroll]);
  const startScrollStart = useCallback(() => startScroll('start'), [startScroll]);

  const handleChevronMouseDown = useCallback(() => {
    mouseDownRef.current = true;
    const onWindowMouseUp = () => {
      mouseDownRef.current = false;
      window.removeEventListener('mouseup', onWindowMouseUp);
    };
    window.addEventListener('mouseup', onWindowMouseUp);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let fadeTimeout: number | undefined;
    const handleScroll = () => {
      if (axis === 'horizontal') {
        setScrolledToEnd(Math.abs(el.scrollLeft + el.clientWidth - el.scrollWidth) < 2);
        setScrolledToStart(el.scrollLeft < 2);
        setHasOverflow(el.scrollWidth > el.clientWidth + 2);
      } else {
        setScrolledToEnd(Math.abs(el.scrollTop + el.clientHeight - el.scrollHeight) < 2);
        setScrolledToStart(el.scrollTop < 2);
        setHasOverflow(el.scrollHeight > el.clientHeight + 2);
      }
      setChevronGlow(true);
      if (fadeTimeout) window.clearTimeout(fadeTimeout);
      fadeTimeout = window.setTimeout(() => setChevronGlow(false), 180);
    };
    el.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => {
      el.removeEventListener('scroll', handleScroll);
      if (fadeTimeout) window.clearTimeout(fadeTimeout);
      stopScroll();
    };
  }, [stopScroll, axis]);

  return { scrollRef, scrolledToEnd, scrolledToStart, hasOverflow, chevronGlow, startScrollEnd, startScrollStart, stopScroll, handleChevronMouseDown };
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface HorizontalProps {
  orientation: 'horizontal';
  sequence: BlockSequence;
  wallBlockIds: Set<string>;
  dragRef: React.MutableRefObject<DragSource | null>;
}

interface VerticalProps {
  orientation: 'vertical';
  sequence: BlockSequence;
  arrowColor?: string;
  editMode?: boolean;
  onBlockTextChange?: (blockSequenceId: string, blockId: string, newText: string) => Promise<void>;
  onBlockCreate?: (blockSequenceId: string, predecessorBlockId: string) => Promise<{ success: boolean; error?: string }>;
  onBlockDelete?: (blockSequenceId: string, blockId: string) => Promise<{ success: boolean; error?: string }>;
  onBlockComplete?: (blockSequenceId: string, blockId: string) => Promise<{ success: boolean; error?: string }>;
}

export type BlockSequenceDisplayProps = HorizontalProps | VerticalProps;

// ─── Vertical variant ─────────────────────────────────────────────────────────

// Height that shows exactly 2 blocks + the arrow between them
const VERTICAL_SCROLL_HEIGHT = SQUARE_SIZE * 2 + 18;

const BlockSequenceDisplayVertical: React.FC<{ sequence: BlockSequence; arrowColor?: string; editMode?: boolean; onBlockTextChange?: (blockSequenceId: string, blockId: string, newText: string) => Promise<void>; onBlockCreate?: (blockSequenceId: string, predecessorBlockId: string) => Promise<{ success: boolean; error?: string }>; onBlockDelete?: (blockSequenceId: string, blockId: string) => Promise<{ success: boolean; error?: string }>; onBlockComplete?: (blockSequenceId: string, blockId: string) => Promise<{ success: boolean; error?: string }> }> = ({ sequence, arrowColor = '#000', editMode = false, onBlockTextChange, onBlockCreate, onBlockDelete, onBlockComplete }) => {
  const color = getCategoryHexColor(sequence.category ?? '');
  const terminalIdx = sequence.blocks.length - 1;
  const useScroll = !editMode && sequence.blocks.length > 3;

  // Insert zone state (only active in edit mode)
  const [hoveredZone, setHoveredZone] = useState<number | null>(null);
  const [insertSuccessSnackbar, setInsertSuccessSnackbar] = useState(false);
  const [insertError, setInsertError] = useState<string | null>(null);

  // Delete block state
  const [blockContextMenu, setBlockContextMenu] = useState<{ mouseX: number; mouseY: number; blockId: string } | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTargetBlockId, setDeleteTargetBlockId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSuccessSnackbar, setDeleteSuccessSnackbar] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Complete block state
  const [completeSuccessSnackbar, setCompleteSuccessSnackbar] = useState(false);
  const [completeError, setCompleteError] = useState<string | null>(null);

  // Close menu and dialog whenever edit mode is turned off
  useEffect(() => {
    if (!editMode) {
      setBlockContextMenu(null);
      setDeleteConfirmOpen(false);
      setDeleteTargetBlockId(null);
      setHoveredZone(null);
    }
  }, [editMode]);

  const handleBlockContextMenu = useCallback((event: React.MouseEvent, blockId: string) => {
    event.preventDefault();
    event.stopPropagation();
    setBlockContextMenu({ mouseX: event.clientX, mouseY: event.clientY, blockId });
  }, []);

  const handleContextMenuClose = useCallback(() => setBlockContextMenu(null), []);

  const handleDeleteMenuClick = useCallback(() => {
    if (!blockContextMenu) return;
    setDeleteTargetBlockId(blockContextMenu.blockId);
    setBlockContextMenu(null);
    setDeleteConfirmOpen(true);
  }, [blockContextMenu]);

  const handleCompleteMenuClick = useCallback(async () => {
    if (!blockContextMenu || !onBlockComplete) return;
    const blockId = blockContextMenu.blockId;
    setBlockContextMenu(null);
    const result = await onBlockComplete(sequence.blockSequenceId, blockId);
    if (result.success) {
      setCompleteSuccessSnackbar(true);
    } else {
      setCompleteError(result.error ?? 'Failed to complete block');
    }
  }, [blockContextMenu, onBlockComplete, sequence.blockSequenceId]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTargetBlockId || !onBlockDelete) return;
    setIsDeleting(true);
    const result = await onBlockDelete(sequence.blockSequenceId, deleteTargetBlockId);
    setIsDeleting(false);
    setDeleteConfirmOpen(false);
    setDeleteTargetBlockId(null);
    if (result.success) {
      setDeleteSuccessSnackbar(true);
    } else {
      setDeleteError(result.error ?? 'Failed to delete block');
    }
  }, [deleteTargetBlockId, onBlockDelete, sequence.blockSequenceId]);

  const handleInsert = useCallback(async (predecessorBlockId: string) => {
    if (!onBlockCreate) return;
    setHoveredZone(null);
    const result = await onBlockCreate(sequence.blockSequenceId, predecessorBlockId);
    if (result.success) {
      setInsertSuccessSnackbar(true);
    } else {
      setInsertError(result.error ?? 'Failed to add block');
    }
  }, [onBlockCreate, sequence.blockSequenceId]);

  // Shared sx for the small + button overlay
  const insertBtnSx = (visible: boolean) => ({
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 28,
    height: 28,
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    opacity: visible ? 1 : 0,
    pointerEvents: visible ? 'auto' : 'none',
    transition: 'opacity 0.15s ease',
    boxShadow: 2,
    zIndex: 10,
    p: 0,
    '&:hover': { bgcolor: 'primary.dark', transform: 'translate(-50%, -50%) scale(1.1)' },
  });

  // Edge zone sx (used before first block and after last block).
  // Reserves fixed space equal to the button so the icon is always reachable.
  const edgeZoneSx = {
    position: 'relative' as const,
    width: SQUARE_SIZE,
    height: 28,
    overflow: 'visible' as const,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };
  const edgeZoneTopSx = edgeZoneSx;
  const edgeZoneBottomSx = edgeZoneSx;

  const { scrollRef, scrolledToEnd, scrolledToStart, chevronGlow, startScrollEnd, startScrollStart, stopScroll, handleChevronMouseDown } = useScrollChevrons('vertical');

  // View mode with scrollable non-terminal blocks (only when > 3 blocks total)
  if (useScroll) {
    const nonTerminalBlocks = sequence.blocks.slice(0, terminalIdx);
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        {/* Top chevron */}
        <Box sx={{ width: SQUARE_SIZE, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', ml: 0.5, mb: 0.5, zIndex: 3, transition: 'filter 0.18s' }}>
          <svg
            width={SQUARE_SIZE}
            height={CHEVRON_THICKNESS}
            viewBox={`0 0 ${SQUARE_SIZE} ${CHEVRON_THICKNESS}`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onMouseEnter={startScrollStart}
            onMouseLeave={stopScroll}
            onMouseDown={handleChevronMouseDown}
            style={{ cursor: 'n-resize', display: 'block', visibility: scrolledToStart ? 'hidden' : 'visible' }}
          >
            <polygon points={`0,${CHEVRON_THICKNESS} ${SQUARE_SIZE / 2},0 ${SQUARE_SIZE},${CHEVRON_THICKNESS}`} fill={chevronGlow ? '#fff' : '#f7f6f3'} />
            {chevronGlow && <polygon points={`0,${CHEVRON_THICKNESS} ${SQUARE_SIZE / 2},0 ${SQUARE_SIZE},${CHEVRON_THICKNESS}`} fill="#fff" opacity={0.25} />}
          </svg>
        </Box>

        {/* Scrollable section for all non-terminal blocks */}
        <Box
          ref={scrollRef}
          sx={{
            overflowY: 'auto',
            height: VERTICAL_SCROLL_HEIGHT,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pl: 2,
            pr: 1,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { backgroundColor: '#f0efed', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#c2bfba', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#a09d98' },
          }}
        >
          {nonTerminalBlocks.map((block, idx) => (
            <React.Fragment key={block.blockId}>
              <BlockCell label={block.text} color={color} />
              {idx < nonTerminalBlocks.length - 1 && <VerticalArrow color={arrowColor} />}
            </React.Fragment>
          ))}
        </Box>

        {/* Bottom chevron — display:none when hidden so the arrow can compensate */}
        {!scrolledToEnd && (
          <Box sx={{ width: SQUARE_SIZE, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', ml: 0.5, mt: 0.5, mb: 0.5, zIndex: 3, transition: 'filter 0.18s' }}>
            <svg
              width={SQUARE_SIZE}
              height={CHEVRON_THICKNESS}
              viewBox={`0 0 ${SQUARE_SIZE} ${CHEVRON_THICKNESS}`}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onMouseEnter={startScrollEnd}
              onMouseLeave={stopScroll}
              onMouseDown={handleChevronMouseDown}
              style={{ cursor: 's-resize', display: 'block' }}
            >
              <polygon points={`0,0 ${SQUARE_SIZE / 2},${CHEVRON_THICKNESS} ${SQUARE_SIZE},0`} fill={chevronGlow ? '#fff' : '#f7f6f3'} />
              {chevronGlow && <polygon points={`0,0 ${SQUARE_SIZE / 2},${CHEVRON_THICKNESS} ${SQUARE_SIZE},0`} fill="#fff" opacity={0.25} />}
            </svg>
          </Box>
        )}

        {/* Frozen terminal block — arrow lengthens when chevron is hidden to keep arrowhead pinned */}
        <VerticalArrowWithLine color={arrowColor} lineLength={scrolledToEnd ? 54 : 28} />
        <BlockCell label={sequence.blocks[terminalIdx].text} color={color} />
      </Box>
    );
  }

  // Edit mode (or ≤ 3 blocks): render all blocks flat with no scroll

  // Zero blocks, not in edit mode — render plain text outside the centered flex container
  if (sequence.blocks.length === 0 && !editMode) {
    return (
      <Typography variant="body2" sx={{ color: 'text.disabled', alignSelf: 'flex-start' }}>
        Edit to add blocks.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Zero-block states */}
      {sequence.blocks.length === 0 && !editMode && (
        <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic', textAlign: 'left', width: '100%' }}>
          Edit to add blocks.
        </Typography>
      )}
      {sequence.blocks.length === 0 && editMode && onBlockCreate && (
        <Tooltip title="Add block" arrow placement="top">
          <IconButton onClick={() => handleInsert('')} sx={{ ...insertBtnSx(true), position: 'relative', transform: 'none', '&:hover': { bgcolor: 'primary.dark', transform: 'scale(1.1)' } }}>
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}

      {/* Zone 0: before first block */}
      {editMode && onBlockCreate && sequence.blocks.length > 0 && (
        <Box
          onMouseEnter={() => setHoveredZone(0)}
          onMouseLeave={() => setHoveredZone(null)}
          sx={edgeZoneTopSx}
        >
          <Tooltip title="Add block" arrow placement="top">
            <IconButton onClick={() => handleInsert('')} sx={insertBtnSx(hoveredZone === 0)}>
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {sequence.blocks.map((block, idx) => (
        <React.Fragment key={block.blockId}>
          <Box
            onContextMenu={(editMode && (onBlockDelete || onBlockComplete)) ? (e) => handleBlockContextMenu(e, block.blockId) : undefined}
            sx={{ position: 'relative' }}
          >
            <BlockCell
              label={block.text}
              color={color}
              onTextSave={editMode && onBlockTextChange ? (newText) => onBlockTextChange(sequence.blockSequenceId, block.blockId, newText) : undefined}
            />
          </Box>
          {/* Connector (with optional insert zone overlay in edit mode) after each non-last block */}
          {idx < terminalIdx && (
            (editMode && onBlockCreate) ? (
              <Box
                onMouseEnter={() => setHoveredZone(idx + 1)}
                onMouseLeave={() => setHoveredZone(null)}
                sx={{ position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', width: SQUARE_SIZE, overflow: 'visible' }}
              >
                {idx < terminalIdx - 1 ? (
                  <VerticalArrow color={arrowColor} />
                ) : (
                  <VerticalArrowWithLine color={arrowColor} />
                )}
                <Tooltip title="Add block" arrow placement="top">
                  <IconButton onClick={() => handleInsert(block.blockId)} sx={insertBtnSx(hoveredZone === idx + 1)}>
                    <AddIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <>
                {idx < terminalIdx - 1 && <VerticalArrow color={arrowColor} />}
                {idx === terminalIdx - 1 && sequence.blocks.length > 1 && <VerticalArrowWithLine color={arrowColor} />}
              </>
            )
          )}
        </React.Fragment>
      ))}

      {/* Right-click context menu for block actions */}
      <Menu
        open={blockContextMenu !== null}
        onClose={handleContextMenuClose}
        anchorReference="anchorPosition"
        anchorPosition={blockContextMenu !== null ? { top: blockContextMenu.mouseY, left: blockContextMenu.mouseX } : undefined}
      >
        {onBlockComplete && (
          <MenuItem
            onClick={handleCompleteMenuClick}
            sx={{ color: 'success.main', '&:hover': { bgcolor: 'success.light', color: 'success.contrastText' } }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 18, mr: 1 }} />
            Complete Block
          </MenuItem>
        )}
        {onBlockDelete && (
          <MenuItem
            onClick={handleDeleteMenuClick}
            sx={{ color: 'error.main', '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' } }}
          >
            <DeleteIcon sx={{ fontSize: 18, mr: 1 }} />
            Delete Block
          </MenuItem>
        )}
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => !isDeleting && setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete Block?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this block? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={isDeleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Zone last: after last block */}
      {editMode && onBlockCreate && sequence.blocks.length > 0 && (
        <Box
          onMouseEnter={() => setHoveredZone(sequence.blocks.length)}
          onMouseLeave={() => setHoveredZone(null)}
          sx={edgeZoneBottomSx}
        >
          <Tooltip title="Add Block" arrow placement="top">
            <IconButton onClick={() => handleInsert(sequence.blocks[terminalIdx].blockId)} sx={insertBtnSx(hoveredZone === sequence.blocks.length)}>
              <AddIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Insert success snackbar */}
      <AirMaskSnackbar open={insertSuccessSnackbar} onClose={() => setInsertSuccessSnackbar(false)} severity="success" message="Block added." />
      {/* Insert error snackbar */}
      <AirMaskSnackbar open={!!insertError} onClose={() => setInsertError(null)} severity="error" message={insertError ?? ''} autoHideDuration={4000} />
      {/* Delete success snackbar */}
      <AirMaskSnackbar open={deleteSuccessSnackbar} onClose={() => setDeleteSuccessSnackbar(false)} severity="success" message="Block deleted." />
      {/* Delete error snackbar */}
      <AirMaskSnackbar open={!!deleteError} onClose={() => setDeleteError(null)} severity="error" message={deleteError ?? ''} autoHideDuration={4000} />
      {/* Complete success snackbar */}
      <AirMaskSnackbar open={completeSuccessSnackbar} onClose={() => setCompleteSuccessSnackbar(false)} severity="success" message="🎉 Block completed! Keep it up!" />
      {/* Complete error snackbar */}
      <AirMaskSnackbar open={!!completeError} onClose={() => setCompleteError(null)} severity="error" message={completeError ?? ''} autoHideDuration={4000} />
    </Box>
  );
};

// ─── Horizontal variant ───────────────────────────────────────────────────────

const CHEVRON_THICKNESS = 14;

const BlockSequenceDisplayHorizontal: React.FC<HorizontalProps> = ({ sequence, wallBlockIds, dragRef }) => {
  const color = getCategoryHexColor(sequence.category ?? '');
  const terminalIdx = sequence.blocks.length - 1;

  // A block is draggable only if it is not already on the wall AND every preceding
  // block in the sequence is already on the wall.
  const isDraggable = (blockIdx: number): boolean => {
    const compositeId = `${sequence.blockSequenceId}#${sequence.blocks[blockIdx].blockId}`;
    if (wallBlockIds.has(compositeId)) return false;
    for (let i = 0; i < blockIdx; i++) {
      const predId = `${sequence.blockSequenceId}#${sequence.blocks[i].blockId}`;
      if (!wallBlockIds.has(predId)) return false;
    }
    return true;
  };  const { scrollRef, scrolledToEnd, scrolledToStart, hasOverflow, chevronGlow, startScrollEnd, startScrollStart, stopScroll, handleChevronMouseDown } = useScrollChevrons('horizontal');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', pt: '3px', pl: '3px' }}>
      {/* Single-block shortcut — no scroll, no arrow. pl matches the hidden left-chevron width (18px + 0.5 gap) */}
      {sequence.blocks.length === 1 && (() => {
        const block0 = sequence.blocks[0];
        const compositeId = `${sequence.blockSequenceId}#${block0.blockId}`;
        const dimmed = wallBlockIds.has(compositeId);
        const canDrag = isDraggable(0);
        return (
          <Box sx={{ pl: '18px' }}>
            <BlockCell
              label={block0.text}
              color={color}
              draggable={canDrag}
              dimmed={dimmed}
              onDragStart={
                canDrag
                  ? (_e: React.DragEvent) => { dragRef.current = { type: 'source', block: { id: compositeId, label: block0.text, color } }; }
                  : undefined
              }
            />
          </Box>
        );
      })()}

      {sequence.blocks.length > 1 && (<>
      {/* Left chevron — only shown when content overflows */}
      <Box sx={{ width: 18, height: SQUARE_SIZE, display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'flex-start', ml: 0, mr: 0.5, zIndex: 3, transition: 'filter 0.18s' }}>
        {hasOverflow && (
          <svg
            width={CHEVRON_THICKNESS}
            height={SQUARE_SIZE}
            viewBox={`0 0 ${CHEVRON_THICKNESS} ${SQUARE_SIZE}`}
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            onMouseEnter={startScrollStart}
            onMouseLeave={stopScroll}
            onMouseDown={handleChevronMouseDown}
            style={{ cursor: 'w-resize', display: 'block', visibility: scrolledToStart ? 'hidden' : 'visible' }}
          >
            <polygon points={`${CHEVRON_THICKNESS},0 0,${SQUARE_SIZE / 2} ${CHEVRON_THICKNESS},${SQUARE_SIZE}`} fill={chevronGlow ? '#fff' : '#f7f6f3'} />
            {chevronGlow && <polygon points={`${CHEVRON_THICKNESS},0 0,${SQUARE_SIZE / 2} ${CHEVRON_THICKNESS},${SQUARE_SIZE}`} fill="#fff" opacity={0.25} />}
          </svg>
        )}
      </Box>

      {/* Scrollable section — when no overflow includes terminal; when overflow only non-terminal */}
      <Box ref={scrollRef} sx={{ ...scrollbarSx, overflowX: 'auto', flex: '1 1 auto', minWidth: 0, pr: 0.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: 'max-content' }}>
          {sequence.blocks.slice(0, hasOverflow ? terminalIdx : sequence.blocks.length).map((block, colIndex, arr) => {
            const compositeId = `${sequence.blockSequenceId}#${block.blockId}`;
            const dimmed = wallBlockIds.has(compositeId);
            const canDrag = isDraggable(colIndex);
            const isLast = colIndex === arr.length - 1;
            return (
              <React.Fragment key={block.blockId}>
                <BlockCell
                  label={block.text}
                  color={color}
                  draggable={canDrag}
                  dimmed={dimmed}
                  onDragStart={
                    canDrag
                      ? (_e: React.DragEvent) => { dragRef.current = { type: 'source', block: { id: compositeId, label: block.text, color } }; }
                      : undefined
                  }
                />
                {!isLast && (!hasOverflow && colIndex === arr.length - 2 ? <HorizontalArrowWithLine /> : <HorizontalArrow />)}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      {/* Frozen arrow + terminal block — only when content overflows */}
      {hasOverflow && (
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', flexShrink: 0, bgcolor: 'transparent', position: 'relative', zIndex: 2 }}>
          {/* Right chevron */}
          {!scrolledToEnd && (
            <Box sx={{ width: 18, height: SQUARE_SIZE, display: 'flex', alignItems: 'center', justifyContent: 'center', alignSelf: 'flex-start', mr: 0.5, zIndex: 3, transition: 'filter 0.18s' }}>
              <svg
                width={CHEVRON_THICKNESS}
                height={SQUARE_SIZE}
                viewBox={`0 0 ${CHEVRON_THICKNESS} ${SQUARE_SIZE}`}
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                onMouseEnter={startScrollEnd}
                onMouseLeave={stopScroll}
                onMouseDown={handleChevronMouseDown}
                style={{ cursor: 'e-resize', display: 'block' }}
              >
                <polygon points={`0,0 ${CHEVRON_THICKNESS},${SQUARE_SIZE / 2} 0,${SQUARE_SIZE}`} fill={chevronGlow ? '#fff' : '#f7f6f3'} />
                {chevronGlow && <polygon points={`0,0 ${CHEVRON_THICKNESS},${SQUARE_SIZE / 2} 0,${SQUARE_SIZE}`} fill="#fff" opacity={0.25} />}
              </svg>
            </Box>
          )}
          <HorizontalArrowWithLine lineLength={scrolledToEnd ? 50 : 28} />
          {(() => {
            const terminalBlock = sequence.blocks[terminalIdx];
            const compositeId = `${sequence.blockSequenceId}#${terminalBlock.blockId}`;
            const dimmed = wallBlockIds.has(compositeId);
            const canDrag = isDraggable(terminalIdx);
            return (
              <BlockCell
                label={terminalBlock.text}
                color={color}
                draggable={canDrag}
                dimmed={dimmed}
                onDragStart={canDrag ? (_e: React.DragEvent) => { dragRef.current = { type: 'source', block: { id: compositeId, label: terminalBlock.text, color } }; } : undefined}
              />
            );
          })()}
        </Box>
      )}
      </>)}
    </Box>
  );
};

// ─── Public component ─────────────────────────────────────────────────────────

const BlockSequenceDisplay: React.FC<BlockSequenceDisplayProps> = (props) => {
  if (props.orientation === 'vertical') {
    return <BlockSequenceDisplayVertical sequence={props.sequence} arrowColor={props.arrowColor} editMode={props.editMode} onBlockTextChange={props.onBlockTextChange} onBlockCreate={props.onBlockCreate} onBlockDelete={props.onBlockDelete} onBlockComplete={props.onBlockComplete} />;
  }
  return <BlockSequenceDisplayHorizontal {...(props as HorizontalProps)} />;
};

export default BlockSequenceDisplay;
